import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import {
  joinReactNodes,
  reorder,
  useSearchInput,
  useTranslations,
} from "@jugl-web/utils";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import React, { FC, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { SearchInput } from "@jugl-web/ui-components/cross-platform/SearchInput";
import {
  Button,
  InteractiveContainer,
  Menu,
  PlainButton,
  Tooltip,
} from "@jugl-web/ui-components";
import { DraggableFieldBoxContainer } from "@jugl-web/domain-resources/custom-fields/components/DraggableFieldBoxContainer";
import { ReactComponent as ReorderIcon } from "@jugl-web/domain-resources/custom-fields/components/DraggableFieldBoxContainer/assets/reorder.svg";
import { FieldBox } from "@jugl-web/domain-resources/custom-fields/components/FieldBox";
import { UserCustomField, useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import Highlighter from "react-highlight-words";
import { useCustomFieldTypes } from "@jugl-web/domain-resources/custom-fields/hooks/useCustomFieldTypes";
import { CustomFieldIcon } from "@jugl-web/domain-resources/custom-fields/components/CustomFieldIcon";
import { ReactComponent as PlusIcon } from "../assets/plus.svg";
import { ReactComponent as MenuIcon } from "../assets/menu.svg";
import { ReactComponent as BookmarkIcon } from "../assets/bookmark.svg";
import { useDefaultUserFields } from "../hooks/useDefaultUserFields";
import { ManageProfileAttributesDialogScreenToParametersMap } from "../types";
import {
  MAX_USER_BOOKMARKS_COUNT,
  MAX_USER_CUSTOM_FIELDS_COUNT,
} from "../consts";

export const EntryScreen: FC<{
  userCustomFields: UserCustomField[];
  bookmarks: UserCustomField[];
  propId: string;
}> = ({ propId, userCustomFields, bookmarks }) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { searchQuery, inputProps, reset } = useSearchInput();
  const { defaultUserFields } = useDefaultUserFields();
  const { customFieldTypeToDetails } = useCustomFieldTypes();
  const { transitionTo } =
    useScreenTransitionManager<ManageProfileAttributesDialogScreenToParametersMap>();

  const { usersApi } = useRestApiProvider();

  const [updateUserCustomFields] = usersApi.useUpdateUserCustomFieldsMutation();

  const [activeDraggableId, setActiveDraggableId] = useState<string | null>(
    null
  );

  const sensors = useSensors(useSensor(MouseSensor));

  const [deleteUserCustomField] = usersApi.useDeleteUserCustomFieldMutation();

  const handleDeleteUserCustomField = useCallback(
    (customFieldId: string) =>
      deleteUserCustomField({
        customFieldId,
        entityId,
        propId,
      }),
    [propId, entityId, deleteUserCustomField]
  );

  const { visibleDefaultUserFields, visibleUserCustomFields } = useMemo(() => {
    if (!searchQuery) {
      return {
        visibleDefaultUserFields: defaultUserFields,
        visibleUserCustomFields: userCustomFields,
      };
    }

    return {
      visibleDefaultUserFields: defaultUserFields.filter((userDefaultField) =>
        userDefaultField.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
      visibleUserCustomFields: userCustomFields.filter((userCustomField) =>
        userCustomField.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    };
  }, [searchQuery, userCustomFields, defaultUserFields]);

  const activeDraggableField = useMemo(
    () =>
      visibleUserCustomFields.find((field) => field.id === activeDraggableId),
    [activeDraggableId, visibleUserCustomFields]
  );

  const sortableFieldIds = useMemo(
    () => visibleUserCustomFields.map((field) => field.id),
    [visibleUserCustomFields]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeField = visibleUserCustomFields.find(
        (field) => field.id === event.active.id
      );

      if (activeField) {
        setActiveDraggableId(activeField.id);
      }
    },
    [visibleUserCustomFields]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDraggableId(null);

      const { active, over } = event;
      const activeIndex = active.data.current?.index;
      const overIndex = over?.data.current?.index;

      if (
        typeof activeIndex !== "number" ||
        typeof overIndex !== "number" ||
        activeIndex === overIndex
      ) {
        return;
      }

      updateUserCustomFields({
        value: reorder(userCustomFields, activeIndex, overIndex).map(
          (customField, idx) => ({
            ...customField,
            order: idx + 1,
          })
        ),
        entityId,
        propId,
      });
    },
    [entityId, propId, updateUserCustomFields, userCustomFields]
  );

  const renderCustomFieldBox = useCallback(
    (customField: UserCustomField) => {
      const subtitle =
        customField.type === "dropdown"
          ? t(
              {
                id: "cpanel-page.values-count",
                defaultMessage: `{count} {count, plural, one {Value} other {Values}}`,
              },
              { count: customField.values.length }
            )
          : undefined;

      return (
        <FieldBox
          title={
            <div className="flex items-center gap-2">
              <CustomFieldIcon type={customField.type} />
              <Highlighter
                autoEscape
                highlightClassName="text-primary font-semibold"
                highlightTag="span"
                textToHighlight={customField.name}
                searchWords={[searchQuery || ""]}
              />
            </div>
          }
          subtitle={joinReactNodes(
            [customFieldTypeToDetails[customField.type].fullLabel, subtitle],
            <div className="h-4 w-px bg-[#E0E0E0]" />
          )}
          customEndSlot={
            <div className="flex items-center gap-4">
              {customField.bookmark > 0 && (
                <Tooltip
                  placement="bottom-end"
                  renderTrigger={({ ref, props }) => (
                    <PlainButton
                      ref={ref}
                      {...props}
                      className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-grey-200"
                      onClick={() => transitionTo({ name: "bookmarks" })}
                    >
                      <BookmarkIcon />
                    </PlainButton>
                  )}
                >
                  {t({
                    id: "cpanel-page.bookmarked-field",
                    defaultMessage: "Bookmarked Field",
                  })}
                </Tooltip>
              )}
              <Menu
                placement="bottom-end"
                sections={[
                  [
                    {
                      id: "edit",
                      label: t({
                        id: "cpanel-page.edit-field",
                        defaultMessage: "Edit Field",
                      }),
                      onSelect: () =>
                        transitionTo({
                          name: "editUserCustomField",
                          userCustomFieldId: customField.id,
                        }),
                    },
                    {
                      id: "delete",
                      label: t({
                        id: "cpanel-page.delete-field",
                        defaultMessage: "Delete Field",
                      }),
                      onSelect: () =>
                        handleDeleteUserCustomField(customField.id),
                    },
                  ],
                ]}
                autoClose
                renderTrigger={({ Trigger, triggerRef }) => (
                  <Trigger ref={triggerRef} as={PlainButton}>
                    <MenuIcon />
                  </Trigger>
                )}
              />
            </div>
          }
        />
      );
    },
    [
      customFieldTypeToDetails,
      handleDeleteUserCustomField,
      searchQuery,
      t,
      transitionTo,
    ]
  );

  return (
    <BottomCenteredDrawer.Content className="animate-fade-in p-10">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between px-2">
          <SearchInput
            variant="filled"
            color="grey"
            className="w-[280px]"
            {...inputProps}
            onReset={reset}
            onClear={reset}
          />
          <div className="flex items-center gap-4">
            <InteractiveContainer
              className="flex h-10 items-center justify-center gap-1.5 rounded-[10px] bg-grey-200 px-[14px] transition-colors hover:bg-grey-300"
              onClick={() => transitionTo({ name: "bookmarks" })}
            >
              <BookmarkIcon />
              <span className="font-secondary text-sm leading-[21px] text-dark-600">
                {t({
                  id: "cpanel-page.bookmarks",
                  defaultMessage: "Bookmarks",
                })}
              </span>
              <div className="flex h-[19px] items-center justify-center rounded-2xl bg-dark/5 px-2">
                <span className="font-secondary text-xs leading-[160%] text-dark-700">
                  {bookmarks.length}/{MAX_USER_BOOKMARKS_COUNT}
                </span>
              </div>
            </InteractiveContainer>
            {userCustomFields.length >= MAX_USER_CUSTOM_FIELDS_COUNT ? (
              <Tooltip
                renderTrigger={({ props, ref }) => (
                  <div
                    className="flex h-10 items-center rounded-lg bg-dark-100 px-8 transition-colors hover:bg-[#D6DDE1]"
                    ref={ref}
                    {...props}
                  >
                    <span className="font-secondary text-sm leading-2 text-dark">
                      {t(
                        {
                          id: "cpanel-page.max-custom-attrubutes-created",
                          defaultMessage:
                            "⚠️ {maxAttributesCount}/{maxAttributesCount} Fields Created",
                        },
                        { maxAttributesCount: MAX_USER_CUSTOM_FIELDS_COUNT }
                      )}
                    </span>
                  </div>
                )}
              >
                {t(
                  {
                    id: "cpanel-page.max-custom-attrubutes-reached-description",
                    defaultMessage:
                      "Profile already contain {maxAttributesCount} of {maxAttributesCount} Custom attributes created. To add new Fields, please delete existing ones first ✅",
                  },
                  { maxAttributesCount: MAX_USER_CUSTOM_FIELDS_COUNT }
                )}
              </Tooltip>
            ) : (
              <Button
                className="h-10"
                iconStart={<PlusIcon />}
                onClick={() => transitionTo({ name: "newUserCustomField" })}
              >
                {t({
                  id: "common.add",
                  defaultMessage: "Add",
                })}
              </Button>
            )}
          </div>
        </div>
        {!visibleDefaultUserFields.length &&
          !visibleUserCustomFields.length && (
            <span className="mt-16 text-center font-secondary text-base leading-4 text-[#828282]">
              {t({
                id: "cpanel-page.no-results",
                defaultMessage: "No results 😔",
              })}
            </span>
          )}
        <div className="flex flex-col gap-4">
          {visibleDefaultUserFields.map((defaultField) => (
            <FieldBox
              key={defaultField.id}
              className="cursor-default"
              title={
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-white">
                    {React.createElement(
                      customFieldTypeToDetails[defaultField.type].primaryIcon
                    )}
                  </div>
                  <Highlighter
                    autoEscape
                    highlightClassName="text-primary font-semibold"
                    highlightTag="span"
                    textToHighlight={defaultField.name}
                    searchWords={[searchQuery || ""]}
                  />
                </div>
              }
              subtitle={customFieldTypeToDetails[defaultField.type].fullLabel}
              customEndSlot={
                <span className="font-secondary text-xs text-grey-700">
                  {t({
                    id: "cpanel-page.default-field",
                    defaultMessage: "Default Field",
                  })}
                </span>
              }
            />
          ))}
        </div>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sortableFieldIds}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex flex-col gap-4">
              {visibleUserCustomFields.map((customField, idx) => (
                <DraggableFieldBoxContainer
                  key={customField.id}
                  id={customField.id}
                  index={idx}
                >
                  {renderCustomFieldBox(customField)}
                </DraggableFieldBoxContainer>
              ))}
            </div>
          </SortableContext>
          {createPortal(
            <DragOverlay style={{ zIndex: 2001 }}>
              {activeDraggableField && (
                <div className="relative">
                  <PlainButton className="absolute top-1/2 -left-0.5 -translate-x-full -translate-y-1/2 cursor-grab">
                    <ReorderIcon />
                  </PlainButton>
                  {renderCustomFieldBox(activeDraggableField)}
                </div>
              )}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </div>
    </BottomCenteredDrawer.Content>
  );
};
